import {Box, Button, Paper, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {Survey, SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {SurveyFormatElement} from "../../common/uiElements/SurveyFormatElement";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {isScreen} from "../shared/surveyQuestions.util";
import {getSurveyQuestionTypeIcon} from "../shared/surveyQuestionTypes.util";

interface Props {
    survey: Survey
}

const COLLAPSED_COUNT = 4;

const SurveyQuestionsPreview = ({survey}: Props) => {

    const {t, i18n} = useTranslation()
    const [expanded, setExpanded] = useState(false)

    const language = i18n.language
    const previewLanguage = survey.languages?.includes(language) ? language : survey.languages?.[0]

    const localizeTitle = useMemo(() => (text?: LocalizedText) => {
        if (!text) return undefined
        const raw = (previewLanguage && text[previewLanguage])
            || text[language]
            || text['en']
            || Object.values(text)[0]
        return surveyMarkdownToPlainText(raw)
    }, [language, previewLanguage])

    const questions = survey.questions ?? []
    const welcome = questions.find(question => question.type === SurveyQuestionType.WELCOME)
    const answerable = questions.filter(question => !isScreen(question))

    const visible = expanded ? answerable : answerable.slice(0, COLLAPSED_COUNT)
    const hiddenCount = answerable.length - visible.length

    const welcomeTitle = localizeTitle(welcome?.title)?.trim()

    const questionRow = (question: SurveyQuestion, index: number) => {
        const title = localizeTitle(question.title)?.trim() || t('Untitled question')

        return (
            <Stack
                key={question.id ?? `${index}-${question.type ?? 'unknown'}`}
                direction={'row'}
                alignItems={'center'}
                gap={1}
                sx={{minWidth: 0}}
            >
                {getSurveyQuestionTypeIcon(question.type)}
                <Typography variant={'body2'} noWrap sx={{minWidth: 0}}>
                    {index + 1}. {title}
                </Typography>

                {/* Outside the noWrap title so a long title truncates around them, not over them. */}
                <SurveyQuestionMarkers question={question} size={17} gap={0}/>
            </Stack>
        )
    }

    const emptyState = (
        <Typography variant={'body2'} color={'text.secondary'}>
            {t('No questions yet - open the editor to build the form.')}
        </Typography>
    )

    return (
        <Paper
            variant={'outlined'}
            sx={{
                p: 2.5,
                borderRadius: 2,
                width: '100%',
                minWidth: 0,
            }}
        >
            <Stack gap={1.25}>
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                    gap={1.5}
                >
                    <Typography variant={'subtitle1'} fontWeight={500} noWrap sx={{minWidth: 0}}>
                        {welcomeTitle}
                    </Typography>

                    <Box sx={{flexShrink: 0}}>
                        <SurveyFormatElement format={survey.format}/>
                    </Box>
                </Stack>

                {answerable.length === 0 ? emptyState : visible.map(questionRow)}

                {(hiddenCount > 0 || expanded) && (
                    <Button
                        size={'small'}
                        onClick={() => setExpanded(current => !current)}
                        sx={{alignSelf: 'flex-start', textTransform: 'none'}}
                    >
                        {expanded
                            ? t('Show less')
                            : t('Show {{n}} more', {n: hiddenCount})}
                    </Button>
                )}
            </Stack>
        </Paper>
    )
}

export default SurveyQuestionsPreview
